import { useState, useEffect, useRef } from "react"; // 🛠️ Added useRef
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  adminLogo,
  arrowdown,
  blog,
  category,
  feedback,
  home,
  menu,
  post,
  profile,
  report,
  stories,
  users,
} from "../assets/image";
import LogoutModel from "../pages/model/LogoutModel";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "../api/queryKeys";

const AdminLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [adminData, setAdminData] = useState({});
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const [isLogout, setIslogout] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsSidebarOpen(window.innerWidth >= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const data = localStorage.getItem("adminData");
    setAdminData(JSON.parse(data));
  }, []);

  const handleConfirmLogOut = async () => {
    await queryClient.clear();
    await queryClient.resetQueries({ queryKey: [] });
    for (const key of Object.values(QUERY_KEYS)) {
      await queryClient.removeQueries({ queryKey: key });
    }
    navigate("/Login");
  };

  const handleCancelLogout = () => {
    setIslogout(false);
  };
  // Close sidebar on mobile when route changes
  useEffect(() => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  }, [location, isMobile]);

  // 🛠️ Handle click outside dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const menuItems = [
    {
      title: "Dashboard",
      icon: <img src={home} alt="" className="w-5 h-5" />,
      path: "/admin/dashboard",
    },
    {
      title: "Users",
      icon: <img src={users} alt="" className="w-6 h-6" />,
      path: "/admin/user/user-list",
    },
    {
      title: "Category",
      icon: <img src={category} alt="" className="w-5 h-5" />,
      path: "/admin/Category",
    },
    {
      title: "Report",
      icon: <img src={report} alt="" className="w-5 h-5" />,
      path: "/admin/Report",
    },
    {
      title: "Posts",
      icon: <img src={post} alt="" className="w-5 h-5" />,
      path: "/admin/post/Posts",
    },
    {
      title: "Blogs",
      icon: <img src={blog} alt="" className="w-5 h-5" />,
      path: "/admin/blog/blogs",
    },
    {
      title: "Inspiring Stories",
      icon: <img src={stories} alt="" className="w-5 h-5" />,
      path: "/admin/story/stories",
    },
    {
      title: "FeedBack",
      icon: <img src={feedback} alt="" className="w-7 h-7" />,
      path: "/admin/feedBack/feedback",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Overlay for mobile */}
      {isMobile && isSidebarOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-20" onClick={() => setIsSidebarOpen(false)} />
      )}

      {/* Header */}
      <header className="bg-white shadow-sm fixed w-full z-30">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="text-grey hover:text-gray-600 focus:outline-none focus:text-gray-600"
              aria-label="Toggle sidebar"
            >
              <img src={menu} alt="" />
            </button>
            <div className="flex items-center gap-2">
              <img src={adminLogo} alt="" className="h-6" />
              <span className="text-xl font-bold text-primary hidden sm:inline">B3longly</span>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Profile Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2 text-grey hover:text-gray-600 focus:outline-none focus:text-gray-600"
              >
                <img src={profile} alt="Admin" className="w-8 h-8 rounded-full" />
                <span className="font-medium hidden sm:inline">{adminData?.name}</span>
                <img src={arrowdown} alt="" />
              </button>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div
                  ref={dropdownRef} // 🛠️ Attached ref here
                  className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-50"
                >
                  <hr className="my-1" />
                  <button
                    onClick={() => {
                      setIsDropdownOpen(false);
                      navigate("/admin/change-password");
                    }}
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Change Password
                  </button>
                  <button
                    onClick={() => {
                      setIslogout(!isLogout);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-gray-100"
                  >
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 z-20 h-full bg-white shadow-lg transform transition-transform duration-300 ease-in-out ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        } ${isMobile ? "w-60" : "w-60"}`}
        style={{ paddingTop: "4rem" }}
      >
        <div className="h-full overflow-y-auto">
          <nav className="px-4 py-4">
            <div className="space-y-1">
              {menuItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                      isActive ? "text-primary bg-blue-50" : "text-dark hover:bg-blue-50 hover:text-primary"
                    }`}
                  >
                    {item.icon}
                    <span>{item.title}</span>
                    {isActive && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-primary"></span>}
                  </Link>
                );
              })}
            </div>
          </nav>
        </div>
      </aside>

      {/* Main Content */}
      <main
        className={`transition-all duration-300 ${isSidebarOpen ? (isMobile ? "ml-0" : "ml-60") : "ml-0"}`}
        style={{ paddingTop: "4rem" }}
      >
        <div className="p-4">
          <Outlet />
        </div>
      </main>
      {isLogout && <LogoutModel handleConfirmLogOut={handleConfirmLogOut} handleCancelLogout={handleCancelLogout} />}
    </div>
  );
};

export default AdminLayout;
